import { useCallback, useEffect, useRef, useState } from 'react';

import { useNavigation } from 'react-router-dom';

import { RedirectSingleton, RedirectType } from '@pkg/classes';
import { useAppSelector } from '@pkg/hooks';

import { useFlowChecks } from '@app/routes/hooks/useFlowChecks';
import { useHandlePath } from '@app/routes/hooks/useHandlePath';
import { NavigationState } from '@app/routes/types';
import { useUser } from '@app/store/zustand';

import { YAT_PICKER } from '../paths';

// This hook is to check for the conditions that call for a redirect
// anywhere in the system. Specifically:
// - Set user password
// - Set primary yat
// - Pod invite
// - Transfer route where you shouldn't be

enum RedirectOptions {
    Create = YAT_PICKER,
    TwitterConnect = '/twitter-connect',
    SetPassword = '/set-password',
}

type DefaultSystemRedirect = string;

export function useCheckUserGlobalRedirects() {
    const newAuthEnabled = useAppSelector((state) => state.featureFlags.features.newAuthEnabled);

    const { transferRedirect, twitterRedirect, passwordRequired } = useFlowChecks();
    const { handleCheckPath } = useHandlePath();

    const navigation = useNavigation();
    const { isLoading: isUserLoading } = useUser();

    const [redirects, setRedirects] = useState<string[]>([]);
    const [redirectTo, setRedirectTo] = useState<string>();

    const systemRedirectPath = RedirectSingleton.getRedirect(RedirectType.Default);
    const redirectsRef = useRef<string[]>([]);

    const consumeRedirect = useCallback(
        (path: string) => {
            if (isUserLoading) return;
            const redirect = handleCheckPath(path);
            if (!redirect) {
                // don't store or set the redirect if we're on the route already
                redirectsRef.current = redirectsRef?.current?.filter((p) => p !== redirect);
                setRedirects((prevRedirects) => prevRedirects?.filter((redirect) => redirect !== path));
            } else {
                if (navigation.state === NavigationState.Idle) {
                    setRedirectTo(decodeURIComponent(redirect));
                }
                return;
            }
        },
        [handleCheckPath, isUserLoading, navigation.state],
    );

    useEffect(() => {
        if (redirects?.length > 0) {
            consumeRedirect(redirects[0]);
        } else {
            setRedirectTo(undefined);
        }
    }, [consumeRedirect, redirects]);

    const handleRedirectPath = useCallback(
        (redirect?: RedirectOptions | DefaultSystemRedirect) => {
            if (!redirect) return;
            const storedRedirects = redirectsRef.current;
            const alreadyStored = storedRedirects?.includes(redirect);

            if (alreadyStored) {
                consumeRedirect(redirect);
                return;
            }
            if (redirect?.length) {
                redirectsRef.current = [...redirectsRef.current, redirect];
                setRedirects((prevRedirects) => {
                    if (prevRedirects.length) {
                        if (prevRedirects.some((currRedirect) => currRedirect === redirect)) {
                            return prevRedirects?.filter((path) => path !== redirect);
                        }
                        return [...prevRedirects, redirect];
                    }
                    return [redirect];
                });
            }
        },
        [consumeRedirect],
    );

    useEffect(() => {
        if (systemRedirectPath) {
            handleRedirectPath(systemRedirectPath as DefaultSystemRedirect);
        }
    }, [handleRedirectPath, systemRedirectPath]);

    useEffect(() => {
        twitterRedirect && handleRedirectPath(RedirectOptions.TwitterConnect);
    }, [handleRedirectPath, twitterRedirect]);

    useEffect(() => {
        !newAuthEnabled && passwordRequired && handleRedirectPath(RedirectOptions.SetPassword);
    }, [handleRedirectPath, passwordRequired, newAuthEnabled]);

    useEffect(() => {
        transferRedirect && handleRedirectPath('/');
    }, [handleRedirectPath, transferRedirect]);

    return redirectTo;
}
